import React from 'react'
import I18n from 'i18n'
import Field from '../common/Field'
import { MyAxios as axios } from '../../../../MyAxios'
import { readGenericQuestionnaire } from '../../../../common/utils/QuestionnaireUtils'
import { WithQuestionnaires } from '../../../../questionnaires/QuestionnaireProvider'

interface PromisData {
  global01?: string
  global02?: string
  pfa11?: string
  pfa21?: string
  pfa23?: string
  pfa53?: string
  pfc12?: string
  pfb1?: string
  pfa5?: string
  pfa4?: string
  eddep04?: string
  eddep06?: string
  eddep29?: string
  eddep41?: string
  srpper11?: string
  srpper18?: string
  srpper23?: string
  srpper46?: string
  global07?: string
  hi7?: string
  an3?: string
  fatexp41?: string
  fatexp40?: string
  edanx01?: string
  edanx40?: string
  edanx41?: string
  edanx53?: string
}

interface QuestionnaireDefinition {
  content?: {
    questions?: Array<{
      id?: string
    }>
  }
}

interface Questionnaires {
  questionnaireDefinitionUrl: (questionnaireKey: string) => string
  authenticatedHeaders: () => Record<string, string>
}

type PromisQuestionId = keyof PromisData

interface PromisProps {
  data: {
    values: PromisData
    questionnaire?: {
      key?: string
    }
  }
  questionnaires?: Questionnaires
}

interface PromisState {
  questionnaireDefinition: QuestionnaireDefinition | null
}

// Subset shown in the timeline card summary
const PROMIS_TIMELINE_QUESTION_IDS: PromisQuestionId[] = [
  'global01', 'global02', 'pfa11'
]

class Promis extends React.Component<PromisProps, PromisState> {
  constructor (props: PromisProps) {
    super(props)
    this.state = {
      questionnaireDefinition: null
    }
  }

  componentDidMount (): void {
    this.fetchQuestionnaireDefinition()
  }

  componentDidUpdate (prevProps: PromisProps): void {
    const previousQuestionnaireKey = prevProps.data?.questionnaire?.key
    const questionnaireKey = this.props.data?.questionnaire?.key
    if (previousQuestionnaireKey !== questionnaireKey) {
      this.fetchQuestionnaireDefinition()
    }
  }

  fetchQuestionnaireDefinition (): void {
    const questionnaireKey = this.props.data?.questionnaire?.key
    const questionnaireDefinitionUrl = this.props.questionnaires?.questionnaireDefinitionUrl

    if (questionnaireKey == null || questionnaireKey === '' || questionnaireDefinitionUrl == null) return

    axios({
      method: 'GET',
      url: questionnaireDefinitionUrl(questionnaireKey),
      headers: { ...this.props.questionnaires?.authenticatedHeaders(), Accept: 'application/json' }
    }).then((response) => {
      this.setState({ questionnaireDefinition: response.data })
    }).catch((err) => {
      console.log('Error retrieving PROMIS questionnaire definition')
      console.log(err)
    })
  }

  translatedValue (questionId: PromisQuestionId): string | undefined {
    const value = this.props.data.values[questionId]

    if (value === undefined || value === '') return undefined

    const questionnaireDefinition = this.state.questionnaireDefinition
    if (questionnaireDefinition == null || questionnaireDefinition.content == null || !Array.isArray(questionnaireDefinition.content.questions)) {
      return value
    }

    const questionDefinition = questionnaireDefinition.content.questions.find(question => question.id === questionId)
    if (questionDefinition == null) return value

    const localizedQuestionAndAnswer = readGenericQuestionnaire(
      this.props.data,
      {
        ...questionnaireDefinition,
        content: {
          ...questionnaireDefinition.content,
          questions: [questionDefinition]
        }
      },
      I18n.locale
    )

    return localizedQuestionAndAnswer?.[0]?.value ?? value
  }

  render (): React.ReactElement {
    return (
      <>
        <div className='sport-fields'>
          {PROMIS_TIMELINE_QUESTION_IDS.map(questionId => {
            const value = this.translatedValue(questionId)
            if (value == null) return null
            return (
              <Field
                key={questionId}
                title={I18n.t(`questionnaires.promis.${questionId}`)}
                value={value}
              />
            )
          })}
        </div>
      </>
    )
  }
}

export default WithQuestionnaires(Promis)
